import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildSearchDocument } from '../helpers/buildSearchDocument';
import { buildSearchHighlightMap } from '../helpers/buildSearchHighlightMap';
import { findSearchMatches } from '../helpers/findSearchMatches';
import type { TSearchableWord } from '../types/TSearch';

type TOptions = {
  words: TSearchableWord[];
};

export const useTranscriptSearch = ({ words }: TOptions) => {
  const [query, setQuery] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [navigationSignal, setNavigationSignal] = useState(0);
  const debouncedQuery = useDebouncedValue(query, 150);

  const searchDocument = useMemo(() => buildSearchDocument(words), [words]);
  const matches = useMemo(
    () =>
      findSearchMatches(searchDocument, debouncedQuery, {
        caseSensitive,
        wholeWord,
      }),
    [caseSensitive, debouncedQuery, searchDocument, wholeWord],
  );
  const highlightMap = useMemo(() => buildSearchHighlightMap(matches), [matches]);

  useEffect(() => {
    setCurrentMatchIndex(matches.length > 0 ? 0 : -1);
  }, [matches]);

  const goToNextMatch = useCallback(() => {
    setNavigationSignal((signal) => signal + 1);
    setCurrentMatchIndex((index) => {
      if (matches.length === 0) {
        return -1;
      }

      return index < 0 ? 0 : (index + 1) % matches.length;
    });
  }, [matches.length]);

  const goToPreviousMatch = useCallback(() => {
    setNavigationSignal((signal) => signal + 1);
    setCurrentMatchIndex((index) => {
      if (matches.length === 0) {
        return -1;
      }

      return index <= 0 ? matches.length - 1 : index - 1;
    });
  }, [matches.length]);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  const currentMatch = matches[currentMatchIndex] ?? null;
  const matchCount = matches.length;
  const currentMatchNumber = currentMatch === null ? 0 : currentMatchIndex + 1;
  const scrollTarget = useMemo(
    () => (currentMatch ? { match: currentMatch, navigationSignal } : null),
    [currentMatch, navigationSignal],
  );

  return {
    query,
    setQuery,
    caseSensitive,
    setCaseSensitive,
    wholeWord,
    setWholeWord,
    matchCount,
    currentMatchIndex,
    currentMatchNumber,
    highlightMap,
    currentMatch,
    scrollTarget,
    goToNextMatch,
    goToPreviousMatch,
    clearSearch,
  };
};
